import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import dayjs from "dayjs";
import { MailingAddressResponse } from "../../../../../../../../api/services/client/models";
import { MuiBox, MuiTypography } from "../../../../../../../../components";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
} from "../../../../../../../../helpers/constants";
import {
  convertToUserFormat,
  getAmountWithCurrency,
  getValueOrBlank,
  getValueOrDash,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { ProjectFeesAndExpenseRow } from "../../../../../../modelsAndSchemas/Models";
import { projectDownloadProformaInvoiceSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ProformaPdfTemplateWrapper } from "./ProjectProformaPdfTemplate.style";

interface ProjectProformaPdfTemplateProps {
  mailingAddress: MailingAddressResponse;
  billingEmail: string;
  projectName: string;
  clientName: string;
  projectExpenseDetails: ProjectFeesAndExpenseRow[];
  totalInvoiceAmount: number;
  billingCurrency: string;
  paymentTerms?: string;
  header?: string;
  footer1?: string;
  footer2?: string;
  footer3?: string;
}

export const ProjectProformaPdfTemplate = ({
  mailingAddress,
  billingEmail,
  projectName,
  projectExpenseDetails,
  clientName,
  totalInvoiceAmount,
  billingCurrency,
  paymentTerms,
  header,
  footer1,
  footer2,
  footer3,
}: ProjectProformaPdfTemplateProps) => {
  const { useFetchLabel } = useSiteCore();
  const getProjectProformaPdfTemplateLabel = useFetchLabel(
    projectDownloadProformaInvoiceSiteCoreKeys.downloadProformaInvoice,
  );

  const addressChunks = [
    mailingAddress?.address1,
    mailingAddress?.address2,
    mailingAddress?.address3,
    mailingAddress?.countryName,
    mailingAddress?.city,
    mailingAddress?.stateId,
    mailingAddress?.postalCode,
  ];

  const nonEmptyChunks = addressChunks.filter((part) => part?.trim()); // remove empty/undefined
  return (
    <ProformaPdfTemplateWrapper>
      <MuiBox textAlign="right">
        <img
          className="right-management-logo"
          src="/images/footerTalentSolutionImg.png"
        />
      </MuiBox>
      <MuiBox className="sub-title">
        <MuiTypography variant="h3Bold">
          {getProjectProformaPdfTemplateLabel(
            projectDownloadProformaInvoiceSiteCoreKeys.PrivateAndConfidential,
          )}
        </MuiTypography>
      </MuiBox>
      <MuiBox display="flex" justifyContent="space-between" gap={5} mb={4}>
        {/* Left: Address */}
        <MuiBox flex={1}>
          <MuiBox className="header-section">
            <MuiTypography>{clientName}</MuiTypography>
            {nonEmptyChunks.map((part, index) => (
              <MuiTypography key={part}>
                {part}
                {index < nonEmptyChunks.length - 1 ? "," : ""}
              </MuiTypography>
            ))}
          </MuiBox>

          <MuiBox className="email-to-section">
            <MuiTypography className="bold-label">
              {getProjectProformaPdfTemplateLabel(
                projectDownloadProformaInvoiceSiteCoreKeys.emailTo,
              )}
            </MuiTypography>
            <MuiBox>{billingEmail}</MuiBox>
          </MuiBox>

          <MuiBox className="date-section">
            <MuiTypography className="bold-label">
              {getProjectProformaPdfTemplateLabel(
                projectDownloadProformaInvoiceSiteCoreKeys.date,
              )}
            </MuiTypography>
            <MuiBox>
              {convertToUserFormat(dayjs().toString(), DATE_FORMAT.DD_MMM_YYYY)}
            </MuiBox>
          </MuiBox>
        </MuiBox>

        {/* Right: Header */}
        <MuiBox className="header-section right-header" flex={1}>
          <MuiTypography>{header}</MuiTypography>
        </MuiBox>
      </MuiBox>
      <MuiBox className="table-title">
        <MuiTypography variant="headerBold">
          {getProjectProformaPdfTemplateLabel(
            projectDownloadProformaInvoiceSiteCoreKeys.Quote,
          )}
        </MuiTypography>
      </MuiBox>
      <MuiBox className="program-details-section">
        <MuiTypography className="bold-label">
          {getProjectProformaPdfTemplateLabel(
            projectDownloadProformaInvoiceSiteCoreKeys.project,
          )}
          {": "}
        </MuiTypography>
        <MuiBox>{projectName}</MuiBox>
      </MuiBox>
      <Table aria-label="Download proforma invoice pdf table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <MuiTypography variant="h3Bold">
                {getProjectProformaPdfTemplateLabel(
                  projectDownloadProformaInvoiceSiteCoreKeys.feeOrExpenseCategoryColumn,
                )}
              </MuiTypography>
            </TableCell>
            <TableCell align="left">
              <MuiTypography variant="h3Bold">
                {getProjectProformaPdfTemplateLabel(
                  projectDownloadProformaInvoiceSiteCoreKeys.expenseOrFeeNameColumn,
                )}
              </MuiTypography>
            </TableCell>
            <TableCell align="left">
              <MuiTypography variant="h3Bold">
                {getProjectProformaPdfTemplateLabel(
                  projectDownloadProformaInvoiceSiteCoreKeys.expenseCategoryColumn,
                )}
              </MuiTypography>
            </TableCell>
            <TableCell align="left">
              <MuiTypography variant="h3Bold">
                {getProjectProformaPdfTemplateLabel(
                  projectDownloadProformaInvoiceSiteCoreKeys.feeOrExpenseTypeColumn,
                )}
              </MuiTypography>
            </TableCell>
            <TableCell align="right">
              <MuiTypography variant="h3Bold">
                {getProjectProformaPdfTemplateLabel(
                  projectDownloadProformaInvoiceSiteCoreKeys.taxColumn,
                )}
              </MuiTypography>
            </TableCell>
            <TableCell align="right">
              <MuiTypography variant="h3Bold">
                {getProjectProformaPdfTemplateLabel(
                  projectDownloadProformaInvoiceSiteCoreKeys.finalLineItemValueColumn,
                )}
              </MuiTypography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {projectExpenseDetails.map((projectExpense) => (
            <TableRow key={projectExpense.id}>
              <TableCell>
                <MuiTypography variant="h3">
                  {getValueOrDash(projectExpense?.typeName)}
                </MuiTypography>
              </TableCell>
              <TableCell>
                <MuiTypography variant="h3">
                  {getValueOrDash(projectExpense?.name)}
                </MuiTypography>
              </TableCell>
              <TableCell>
                <MuiTypography variant="h3">
                  {getValueOrDash(projectExpense.expenseCategoryName)}
                </MuiTypography>
              </TableCell>
              <TableCell>
                <MuiTypography variant="h3">
                  {getValueOrDash(projectExpense.feeOrExpenseType)}
                </MuiTypography>
              </TableCell>
              <TableCell align="right" className="vertical-align-bottom">
                {projectExpense?.taxDetails?.length
                  ? projectExpense?.taxDetails?.map((projectTaxDetail) => (
                      <MuiTypography
                        variant="h3"
                        className="tax-item"
                        key={projectTaxDetail.taxType?.id}
                      >
                        {projectTaxDetail.taxType?.name}
                      </MuiTypography>
                    ))
                  : EMPTY_VALUE_DASH}
              </TableCell>
              <TableCell align="right" className="vertical-align-bottom">
                <MuiTypography variant="h3">
                  {getAmountWithCurrency(
                    projectExpense.billingAmount,
                    billingCurrency,
                  )}
                </MuiTypography>
                {projectExpense?.taxDetails?.map((projectTaxDetail) => (
                  <MuiTypography variant="h3" key={projectTaxDetail.amount}>
                    {getAmountWithCurrency(
                      projectTaxDetail.amount ?? 0,
                      billingCurrency,
                    )}
                  </MuiTypography>
                ))}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={3}>
              <MuiTypography variant="h3Bold">
                {getProjectProformaPdfTemplateLabel(
                  projectDownloadProformaInvoiceSiteCoreKeys.totalAmountColumn,
                )}
              </MuiTypography>
            </TableCell>
            <TableCell colSpan={2} align="right">
              <MuiTypography variant="h3Bold">
                {getAmountWithCurrency(totalInvoiceAmount, billingCurrency)}
              </MuiTypography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <MuiBox className="payment-notice-section">
        <MuiTypography fontWeight="bold">
          {getProjectProformaPdfTemplateLabel(
            projectDownloadProformaInvoiceSiteCoreKeys.paymentTerms,
          )}
          {": "}
          {getValueOrBlank(paymentTerms)}
        </MuiTypography>
      </MuiBox>

      <MuiBox className="footer-section">
        <MuiTypography>{footer1}</MuiTypography>
        <MuiTypography>{footer2}</MuiTypography>
        <MuiTypography>{footer3}</MuiTypography>
      </MuiBox>
    </ProformaPdfTemplateWrapper>
  );
};
